import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

type AuthMode = 'login' | 'register';
type RegisterStep = 1 | 2;

const AuthPage = () => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [regStep, setRegStep] = useState<RegisterStep>(1);
  const navigate = useNavigate();

  // Login state
  const [loginData, setLoginData] = useState({ phone: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Register state
  const [regData, setRegData] = useState({
    fullName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    category: '',
    city: '',
    agreeTerms: false,
  });
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // OTP state
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);

  const categories = [
    { id: 'tech', label: 'تقنية وبرمجة', icon: 'ri-code-line' },
    { id: 'creative', label: 'إبداع وفنون', icon: 'ri-palette-line' },
    { id: 'business', label: 'ريادة أعمال', icon: 'ri-briefcase-line' },
    { id: 'education', label: 'تعليم', icon: 'ri-book-open-line' },
    { id: 'food', label: 'طعام ومطاعم', icon: 'ri-restaurant-line' },
    { id: 'content', label: 'محتوى رقمي', icon: 'ri-video-line' },
  ];

  const cities = ['الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر', 'الأحساء', 'تبوك', 'أبها'];

  // Login submit
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!loginData.phone || !loginData.password) {
      setLoginError('يرجى إدخال رقم الجوال وكلمة المرور');
      return;
    }
    setLoginLoading(true);
    setTimeout(() => {
      setLoginLoading(false);
      navigate('/dashboard');
    }, 1500);
  };

  // Register step 1 validation
  const canProceedStep1 = () =>
    regData.fullName.trim().length > 2 &&
    regData.phone.trim().length >= 10 &&
    regData.email.includes('@') &&
    regData.password.length >= 8 &&
    regData.password === regData.confirmPassword;

  // Register step 2 validation
  const canProceedStep2 = () =>
    regData.category !== '' && regData.city !== '' && regData.agreeTerms;

  // Register submit
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');
    if (!canProceedStep2()) {
      setRegError('يرجى إكمال جميع الحقول والموافقة على الشروط');
      return;
    }
    setRegLoading(true);
    setTimeout(() => {
      setRegLoading(false);
      setShowOtp(true);
    }, 1500);
  };

  // OTP input handler
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 3) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleOtpVerify = () => {
    if (otp.join('').length === 4) {
      navigate('/dashboard');
    }
  };

  // OTP Screen
  if (showOtp) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6" dir="rtl">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl border border-stone-100 p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
              <i className="ri-smartphone-line text-3xl text-emerald-600"></i>
            </div>
            <h2 className="text-xl font-black text-stone-800 mb-2">تحقق من رقم جوالك</h2>
            <p className="text-stone-500 text-sm mb-1">أرسلنا رمز التحقق إلى</p>
            <p className="text-stone-800 font-bold text-sm mb-6">{regData.phone}</p>

            {/* OTP Inputs */}
            <div className="flex justify-center gap-3 mb-6" dir="ltr">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  className="w-12 h-12 text-center text-xl font-black border-2 border-stone-200 rounded-xl focus:outline-none focus:border-emerald-500 text-stone-800 transition-colors"
                />
              ))}
            </div>

            <button
              onClick={handleOtpVerify}
              disabled={otp.join('').length < 4}
              className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed mb-4"
            >
              تحقق وأكمل التسجيل
            </button>

            <button
              onClick={() => setShowOtp(false)}
              className="text-sm text-stone-400 hover:text-stone-600 cursor-pointer"
            >
              تغيير رقم الجوال
            </button>

            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-xs text-stone-400">لم تستلم الرمز؟</p>
              <button className="text-xs text-emerald-600 font-semibold cursor-pointer hover:underline mt-1">
                إعادة الإرسال بعد 60 ثانية
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex" dir="rtl">

      {/* Left Panel - Decorative (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Saudi%20Arabia%20creative%20entrepreneurs%20community%20warm%20golden%20light%20inspiring%20success%20modern%20professional%20diverse%20people%20smiling&width=800&height=1000&seq=701&orientation=portrait"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-emerald-900/80 via-emerald-800/60 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <Link to="/" className="mb-auto pt-8">
            <img
              src="https://static.readdy.ai/image/d635066a0d023485209a64426ae9d593/51fc048e934e5df43d37533356a4b33b.png"
              alt="هِبة"
              className="h-12 w-auto object-contain brightness-0 invert"
            />
          </Link>
          <div>
            <h2 className="text-3xl font-black mb-3 leading-tight">
              انضم لمجتمع<br />
              <span className="text-emerald-300">المبدعين العرب</span>
            </h2>
            <p className="text-white/80 text-sm leading-relaxed mb-8">
              أكثر من 2,000 مبدع يحققون أحلامهم عبر هِبة. ابدأ رحلتك اليوم.
            </p>
            <div className="flex flex-col gap-3">
              {[
                { icon: 'ri-shield-check-line', text: 'منصة موثوقة ومرخصة في المملكة' },
                { icon: 'ri-bank-card-line', text: 'دعم Apple Pay و Mada و STC Pay' },
                { icon: 'ri-user-heart-line', text: '+50,000 داعم كريم' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg shrink-0">
                    <i className={`${item.icon} text-emerald-300 text-sm`}></i>
                  </div>
                  <span className="text-white/90 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col">

        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center justify-between px-6 pt-6 pb-4">
          <Link to="/">
            <img
              src="https://static.readdy.ai/image/d635066a0d023485209a64426ae9d593/51fc048e934e5df43d37533356a4b33b.png"
              alt="هِبة"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <Link to="/" className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer flex items-center gap-1">
            <i className="ri-arrow-right-line text-xs"></i>
            الرئيسية
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">

            {/* Mode Toggle */}
            <div className="flex bg-stone-100 rounded-2xl p-1 mb-8">
              <button
                onClick={() => { setMode('login'); setRegStep(1); setRegError(''); setLoginError(''); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  mode === 'login' ? 'bg-white text-stone-800' : 'text-stone-500'
                }`}
              >
                تسجيل الدخول
              </button>
              <button
                onClick={() => { setMode('register'); setLoginError(''); setRegError(''); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  mode === 'register' ? 'bg-white text-stone-800' : 'text-stone-500'
                }`}
              >
                إنشاء حساب
              </button>
            </div>

            {/* ── LOGIN FORM ── */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <h2 className="text-2xl font-black text-stone-800 mb-1">أهلاً بعودتك!</h2>
                  <p className="text-stone-500 text-sm">سجّل دخولك للوصول إلى حملاتك</p>
                </div>

                {/* Social Login */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <button type="button" className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-stone-200 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-google-line text-red-500"></i>
                    Google
                  </button>
                  <button type="button" className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-stone-200 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-apple-line text-stone-800"></i>
                    Apple
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-stone-200"></div>
                  <span className="text-xs text-stone-400">أو</span>
                  <div className="flex-1 h-px bg-stone-200"></div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">رقم الجوال</label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={loginData.phone}
                      onChange={(e) => setLoginData({ ...loginData, phone: e.target.value })}
                      placeholder="05xxxxxxxx"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 text-right pr-12"
                      dir="ltr"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-stone-400">
                      <span className="text-xs">🇸🇦</span>
                      <span className="text-xs">+966</span>
                    </div>
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-stone-700">كلمة المرور</label>
                    <button type="button" className="text-xs text-emerald-600 hover:underline cursor-pointer">نسيت كلمة المرور؟</button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 text-right pl-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 cursor-pointer"
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <i className="ri-error-warning-line text-red-500 text-sm shrink-0"></i>
                    <p className="text-red-600 text-xs">{loginError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loginLoading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> جاري تسجيل الدخول...</>
                  ) : (
                    <><i className="ri-login-circle-line"></i> تسجيل الدخول</>
                  )}
                </button>

                <p className="text-center text-xs text-stone-400">
                  ليس لديك حساب؟{' '}
                  <button type="button" onClick={() => setMode('register')} className="text-emerald-600 font-semibold cursor-pointer hover:underline">
                    أنشئ حساباً الآن
                  </button>
                </p>
              </form>
            )}

            {/* ── REGISTER FORM ── */}
            {mode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-black text-stone-800">إنشاء حساب جديد</h2>
                  </div>
                  <p className="text-stone-500 text-sm">
                    الخطوة {regStep} من 2 —{' '}
                    {regStep === 1 ? 'معلوماتك الشخصية' : 'تفاصيل حسابك'}
                  </p>
                  {/* Step Progress */}
                  <div className="flex gap-1 mt-3">
                    <div className="flex-1 h-1 rounded-full bg-emerald-500"></div>
                    <div className={`flex-1 h-1 rounded-full transition-colors ${regStep === 2 ? 'bg-emerald-500' : 'bg-stone-200'}`}></div>
                  </div>
                </div>

                {/* STEP 1 */}
                {regStep === 1 && (
                  <>
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">الاسم الكامل <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={regData.fullName}
                        onChange={(e) => setRegData({ ...regData, fullName: e.target.value })}
                        placeholder="مثال: أحمد محمد الشمري"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 text-right"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">رقم الجوال <span className="text-red-400">*</span></label>
                      <div className="relative">
                        <input
                          type="tel"
                          value={regData.phone}
                          onChange={(e) => setRegData({ ...regData, phone: e.target.value })}
                          placeholder="05xxxxxxxx"
                          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 text-right pr-16"
                          dir="ltr"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-stone-400">
                          <span className="text-xs">🇸🇦</span>
                          <span className="text-xs font-medium">+966</span>
                        </div>
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">البريد الإلكتروني <span className="text-red-400">*</span></label>
                      <input
                        type="email"
                        value={regData.email}
                        onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                        placeholder="example@email.com"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400"
                        dir="ltr"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">كلمة المرور <span className="text-red-400">*</span></label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={regData.password}
                          onChange={(e) => setRegData({ ...regData, password: e.target.value })}
                          placeholder="8 أحرف على الأقل"
                          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 text-right pl-10"
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 cursor-pointer">
                          <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                        </button>
                      </div>
                      {/* Password Strength */}
                      {regData.password.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`flex-1 h-1 rounded-full transition-colors ${
                                regData.password.length >= level * 2
                                  ? level <= 2 ? 'bg-red-400' : level === 3 ? 'bg-amber-400' : 'bg-emerald-500'
                                  : 'bg-stone-200'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">تأكيد كلمة المرور <span className="text-red-400">*</span></label>
                      <div className="relative">
                        <input
                          type={showConfirm ? 'text' : 'password'}
                          value={regData.confirmPassword}
                          onChange={(e) => setRegData({ ...regData, confirmPassword: e.target.value })}
                          placeholder="أعد إدخال كلمة المرور"
                          className={`w-full border rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none text-right pl-10 ${
                            regData.confirmPassword && regData.password !== regData.confirmPassword
                              ? 'border-red-300 focus:border-red-400'
                              : 'border-stone-200 focus:border-emerald-400'
                          }`}
                        />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 cursor-pointer">
                          <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                        </button>
                        {regData.confirmPassword && regData.password === regData.confirmPassword && (
                          <i className="ri-checkbox-circle-line text-emerald-500 absolute left-3 top-1/2 -translate-y-1/2 text-sm"></i>
                        )}
                      </div>
                      {regData.confirmPassword && regData.password !== regData.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">كلمتا المرور غير متطابقتين</p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => canProceedStep1() && setRegStep(2)}
                      disabled={!canProceedStep1()}
                      className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      التالي
                      <i className="ri-arrow-left-line mr-2"></i>
                    </button>
                  </>
                )}

                {/* STEP 2 */}
                {regStep === 2 && (
                  <>
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-3">مجال إبداعك <span className="text-red-400">*</span></label>
                      <div className="grid grid-cols-2 gap-2">
                        {categories.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setRegData({ ...regData, category: cat.id })}
                            className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                              regData.category === cat.id
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                : 'border-stone-200 hover:border-stone-300 text-stone-600'
                            }`}
                          >
                            <i className={`${cat.icon} text-base shrink-0`}></i>
                            <span className="text-xs font-medium leading-tight">{cat.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">مدينتك <span className="text-red-400">*</span></label>
                      <div className="relative">
                        <select
                          value={regData.city}
                          onChange={(e) => setRegData({ ...regData, city: e.target.value })}
                          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 appearance-none bg-white text-right cursor-pointer"
                        >
                          <option value="">اختر مدينتك</option>
                          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <i className="ri-arrow-down-s-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"></i>
                      </div>
                    </div>

                    {/* Terms */}
                    <button
                      type="button"
                      onClick={() => setRegData({ ...regData, agreeTerms: !regData.agreeTerms })}
                      className="flex items-start gap-3 cursor-pointer w-full text-right"
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${regData.agreeTerms ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300'}`}>
                        {regData.agreeTerms && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <span className="text-sm text-stone-600 leading-relaxed">
                        أوافق على{' '}
                        <span className="text-emerald-600 font-medium">شروط الاستخدام</span>
                        {' '}و{' '}
                        <span className="text-emerald-600 font-medium">سياسة الخصوصية</span>
                      </span>
                    </button>

                    {regError && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                        <i className="ri-error-warning-line text-red-500 text-sm shrink-0"></i>
                        <p className="text-red-600 text-xs">{regError}</p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setRegStep(1)}
                        className="flex items-center gap-1 px-4 py-3.5 rounded-xl border border-stone-200 text-stone-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors"
                      >
                        <i className="ri-arrow-right-line text-sm"></i>
                        السابق
                      </button>
                      <button
                        type="submit"
                        disabled={regLoading || !canProceedStep2()}
                        className="flex-1 py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {regLoading ? (
                          <><i className="ri-loader-4-line animate-spin"></i> جاري الإنشاء...</>
                        ) : (
                          <><i className="ri-user-add-line"></i> إنشاء الحساب</>
                        )}
                      </button>
                    </div>
                  </>
                )}

                <p className="text-center text-xs text-stone-400">
                  لديك حساب بالفعل؟{' '}
                  <button type="button" onClick={() => setMode('login')} className="text-emerald-600 font-semibold cursor-pointer hover:underline">
                    سجّل دخولك
                  </button>
                </p>
              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
